import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { T } from '@testout/labsim-ui-atomics';
import LabSimService from '../../services/LabSimService';
import StudentDetailDialog from './StudentDetailDialog';
import styles from './ManualSyncDialog.module.less';
const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;
const TROUBLESHOOTING_URL = 'https://help.comptia.org/hc/en-us/articles/48339878773268-LMS-Integration-Grade-Sync-Troubleshooting';
const CONTACT_SUPPORT_URL = 'https://help.comptia.org/hc/en-us/requests/new';
var SyncToastStatus;
(function (SyncToastStatus) {
    SyncToastStatus["Queued"] = "queued";
    SyncToastStatus["Error"] = "error";
})(SyncToastStatus || (SyncToastStatus = {}));
function statusDotClass(syncStatus) {
    if (syncStatus === 'Success') {
        return styles.dotSuccess;
    }
    if (syncStatus === 'PartialFail') {
        return styles.dotPartial;
    }
    if (syncStatus === 'Fail') {
        return styles.dotFail;
    }
    return styles.dotNone;
}
function statusLabel(syncStatus) {
    if (syncStatus === 'Success') {
        return 'Success';
    }
    if (syncStatus === 'PartialFail') {
        return 'Partial Fail';
    }
    if (syncStatus === 'Fail') {
        return 'Fail';
    }
    return 'Not Attempted';
}
function StudentRow({ student, onClick, }) {
    const handleClick = useCallback(() => {
        onClick(student);
    }, [onClick, student]);
    return (React.createElement("div", { className: styles.studentRow },
        React.createElement("div", { className: styles.studentName },
            React.createElement(T.LinkButton, { automationId: `student-${student.studentId}`, onClick: handleClick }, student.name)),
        React.createElement("div", { className: styles.statusCell },
            React.createElement("span", { className: `${styles.statusDot} ${statusDotClass(student.syncStatus)}`, "aria-hidden": 'true' }),
            React.createElement(T.Text.Span, { automationId: `syncStatus-${student.studentId}`, className: styles.statusLabel }, statusLabel(student.syncStatus))),
        React.createElement(T.IconButton, { className: styles.chevron, screenReaderDescription: `View sync details for ${student.name}`, automationId: `chevron-${student.studentId}`, iconVariation: T.IconVariation.ChevronRight, onClick: handleClick })));
}
function StudentSection({ selectedClassName, searchValue, students, totalCount, loadingStudents, loadingMore, hasMore, onSearchChange, onSyncAll, onStudentClick, onLoadMore, syncDisabled, }) {
    const sentinelRef = useRef(null);
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !hasMore) {
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                onLoadMore();
            }
        }, { threshold: 0.1 });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, onLoadMore]);
    return (React.createElement(React.Fragment, null,
        React.createElement("hr", { className: styles.divider }),
        React.createElement("div", { className: styles.sectionHeader },
            React.createElement(T.Text.H4, { automationId: 'sectionClassName', className: styles.sectionTitle }, selectedClassName),
            React.createElement(T.Input.Search, { className: styles.sectionSearch, automationId: 'studentSearch', onSearch: onSearchChange })),
        React.createElement("div", { className: styles.syncActions },
            React.createElement(T.Button, { automationId: 'syncAllGrades', onClick: onSyncAll, disabled: syncDisabled, variation: T.ButtonVariation.Secondary },
                React.createElement(T.IconText, { automationId: 'syncAllText', variation: T.Text.Variation.Inherit, leftIconVariation: T.IconVariation.Sync }, "Sync All Grades"))),
        loadingStudents && React.createElement(T.Loading.Inline, null),
        !loadingStudents && students.length === 0 && (React.createElement(T.Text.P, { automationId: 'noStudents', className: styles.emptyStudents }, "No students found.")),
        !loadingStudents && students.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: styles.studentTableHeader },
                React.createElement(T.Text.Span, { automationId: 'colStudent', className: styles.studentName }, "Student"),
                React.createElement(T.Text.Span, { automationId: 'colSyncStatus', className: styles.statusCell }, "Sync Status")),
            students.map((s) => (React.createElement(StudentRow, { key: s.studentId, student: s, onClick: onStudentClick }))),
            hasMore && React.createElement("div", { ref: sentinelRef, className: styles.scrollSentinel }),
            loadingMore && React.createElement(T.Loading.Inline, null)))));
}
function ManualSyncDialog({ isOpen, onClose }) {
    var _a, _b, _c, _d;
    const [instructorName, setInstructorName] = useState('');
    const [instructorId, setInstructorId] = useState('');
    const [instructorResults, setInstructorResults] = useState([]);
    const [showInstructorDropdown, setShowInstructorDropdown] = useState(false);
    const [dropdownPortalStyle, setDropdownPortalStyle] = useState({});
    const [loadingUser, setLoadingUser] = useState(true);
    const [selectedClassId, setSelectedClassId] = useState('');
    const [selectedClassName, setSelectedClassName] = useState('');
    const [classes, setClasses] = useState([]);
    const [loadingClasses, setLoadingClasses] = useState(false);
    const [students, setStudents] = useState([]);
    const [totalStudentCount, setTotalStudentCount] = useState(0);
    const [, setCurrentPage] = useState(1);
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [showSyncToast, setShowSyncToast] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const searchDebounceRef = useRef(null);
    const activeSearchRef = useRef('');
    const activePageRef = useRef(1);
    const instructorDebounceRef = useRef(null);
    const instructorWrapperRef = useRef(null);
    useEffect(() => {
        LabSimService.getCurrentUser()
            .then((user) => {
            setInstructorName(user.name);
            setInstructorId(user.userId);
            setLoadingUser(false);
        })
            .catch(() => setLoadingUser(false));
    }, []);
    // Load classes when an instructor is selected; clear when instructor is cleared
    useEffect(() => {
        if (!instructorId) {
            setClasses([]);
            setSelectedClassId('');
            setSelectedClassName('');
            return;
        }
        setLoadingClasses(true);
        LabSimService.getClassesForInstructor(instructorId)
            .then((data) => {
            var _a;
            if (data && !data.error) {
                const classList = (_a = data.classes) !== null && _a !== void 0 ? _a : [];
                setClasses(classList);
                if (classList.length === 1) {
                    setSelectedClassId(classList[0].classProfileId);
                    setSelectedClassName(classList[0].name);
                }
            }
            setLoadingClasses(false);
        })
            .catch(() => setLoadingClasses(false));
    }, [instructorId]);
    const fetchStudents = useCallback((classId, search, page, append = false) => {
        if (append) {
            setLoadingMore(true);
        }
        else {
            setLoadingStudents(true);
        }
        LabSimService.getStudentsInClass(classId, search, page, PAGE_SIZE)
            .then((result) => {
            if (append) {
                setStudents((prev) => [...prev, ...result.students]);
            }
            else {
                setStudents(result.students);
            }
            setTotalStudentCount(result.totalCount);
            setLoadingStudents(false);
            setLoadingMore(false);
        })
            .catch(() => {
            setLoadingStudents(false);
            setLoadingMore(false);
        });
    }, []);
    useEffect(() => {
        if (!selectedClassId) {
            setStudents([]);
            setTotalStudentCount(0);
            return;
        }
        setStudents([]);
        setTotalStudentCount(0);
        setCurrentPage(1);
        setSearchValue('');
        activeSearchRef.current = '';
        activePageRef.current = 1;
        fetchStudents(selectedClassId, '', 1, false);
    }, [selectedClassId, fetchStudents]);
    const handleInstructorInputChange = useCallback((value) => {
        setInstructorName(value);
        setInstructorId('');
        if (instructorDebounceRef.current) {
            clearTimeout(instructorDebounceRef.current);
        }
        if (!value.trim()) {
            setInstructorResults([]);
            setShowInstructorDropdown(false);
            return;
        }
        instructorDebounceRef.current = setTimeout(() => {
            LabSimService.searchInstructors(value)
                .then((results) => {
                setInstructorResults(results);
                if (results.length > 0 && instructorWrapperRef.current) {
                    const rect = instructorWrapperRef.current.getBoundingClientRect();
                    setDropdownPortalStyle({
                        position: 'fixed',
                        top: rect.bottom + 2,
                        left: rect.left,
                        width: rect.width,
                        zIndex: 9999,
                    });
                }
                setShowInstructorDropdown(results.length > 0);
            })
                .catch(() => {
                setInstructorResults([]);
                setShowInstructorDropdown(false);
            });
        }, SEARCH_DEBOUNCE_MS);
    }, []);
    const handleInstructorSelect = useCallback((instructor) => {
        setInstructorName(instructor.name);
        setInstructorId(instructor.userId);
        setInstructorResults([]);
        setShowInstructorDropdown(false);
    }, []);
    const handleInstructorBlur = useCallback(() => {
        setTimeout(() => setShowInstructorDropdown(false), 150);
    }, []);
    const handleSearchChange = useCallback((value) => {
        setSearchValue(value);
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            activeSearchRef.current = value;
            activePageRef.current = 1;
            setCurrentPage(1);
            fetchStudents(selectedClassId, value, 1, false);
        }, SEARCH_DEBOUNCE_MS);
    }, [selectedClassId, fetchStudents]);
    const handleLoadMore = useCallback(() => {
        if (loadingMore || loadingStudents || !selectedClassId) {
            return;
        }
        const nextPage = activePageRef.current + 1;
        activePageRef.current = nextPage;
        setCurrentPage(nextPage);
        fetchStudents(selectedClassId, activeSearchRef.current, nextPage, true);
    }, [loadingMore, loadingStudents, selectedClassId, fetchStudents]);
    const handleClassChange = useCallback((classId) => {
        var _a;
        const found = classes.find((c) => c.classProfileId === classId);
        setSelectedClassId(classId);
        setSelectedClassName((_a = found === null || found === void 0 ? void 0 : found.name) !== null && _a !== void 0 ? _a : '');
    }, [classes]);
    const handleSyncAll = useCallback(() => {
        LabSimService.advantageManualSyncGrades(selectedClassId)
            .then(() => {
            setShowSyncToast(SyncToastStatus.Queued);
        })
            .catch(() => {
            setShowSyncToast(SyncToastStatus.Error);
        });
    }, [selectedClassId]);
    const handleToastHidden = useCallback(() => {
        setShowSyncToast(null);
    }, []);
    const handleStudentClick = useCallback((student) => {
        setSelectedStudent(student);
    }, []);
    const handleStudentDetailClose = useCallback(() => {
        setSelectedStudent(null);
    }, []);
    if (!isOpen) {
        return null;
    }
    const selectedClass = classes.find((c) => c.classProfileId === selectedClassId);
    return (React.createElement(React.Fragment, null,
        React.createElement(T.SimpleDialog, { automationId: 'manualSyncDialog', onClose: onClose, title: React.createElement(T.Text.H3, { automationId: 'sync-class-grades', className: styles.dialogTitle }, "Sync Class Grades") },
            React.createElement(SyncGradesInfo, null),
            React.createElement(T.Form, { automationId: 'manualSyncForm' },
                React.createElement("div", { className: styles.formRow },
                    React.createElement(T.Form.Field, { automationId: 'syncDialogInstructorField', className: styles.formField },
                        React.createElement(T.Form.Label, null,
                            "Instructor Name",
                            ' ',
                            React.createElement("span", { className: styles.requiredMark, "aria-hidden": 'true' }, "Required")),
                        loadingUser ? (React.createElement(T.Loading.Inline, null)) : (React.createElement("div", { ref: instructorWrapperRef, className: styles.instructorSearchWrapper },
                            React.createElement(T.Input.Text, { automationId: 'instructorNameInput', value: instructorName, onChange: handleInstructorInputChange, onBlur: handleInstructorBlur, placeholder: 'Search for an instructor...', autoComplete: 'off', ariaAutocomplete: true, ariaHasPopup: 'listbox', ariaExpanded: showInstructorDropdown })))),
                    React.createElement(T.Form.Field, { automationId: 'syncDialogClassSelect', className: styles.formField },
                        React.createElement(T.Form.Label, null,
                            "Class",
                            ' ',
                            React.createElement("span", { className: styles.requiredMark, "aria-hidden": 'true' }, "Required")),
                        loadingClasses ? (React.createElement(T.Loading.Inline, null)) : (React.createElement(T.Form.Select, { value: selectedClassId, onChange: handleClassChange, placeholder: 'Select a class...', disabled: !instructorId }, classes.map((cls) => (React.createElement(T.Select.Option, { key: cls.classProfileId, value: cls.classProfileId }, cls.name)))))))),
            selectedClassId && (React.createElement(StudentSection, { selectedClassName: selectedClassName, searchValue: searchValue, students: students, totalCount: totalStudentCount, loadingStudents: loadingStudents, loadingMore: loadingMore, hasMore: students.length < totalStudentCount, onSearchChange: handleSearchChange, onSyncAll: handleSyncAll, syncDisabled: !instructorId, onStudentClick: handleStudentClick, onLoadMore: handleLoadMore })),
            showSyncToast === SyncToastStatus.Queued && (React.createElement(T.SimpleToast, { onHidden: handleToastHidden }, "Grade sync queued. Sync can take several minutes. Please check back later.")),
            showSyncToast === SyncToastStatus.Error && (React.createElement(T.SimpleToast, { onHidden: handleToastHidden }, "Grade sync could not be queued. Please try again."))),
        showInstructorDropdown &&
            instructorResults.length > 0 &&
            ReactDOM.createPortal(React.createElement("ul", { style: dropdownPortalStyle, className: styles.instructorDropdown, role: 'listbox', "aria-label": 'Instructor suggestions' }, instructorResults.map((instructor) => (React.createElement("li", { key: instructor.userId, role: 'option', "aria-selected": false, className: styles.instructorDropdownItem, onMouseDown: () => handleInstructorSelect(instructor) }, instructor.name)))), document.body),
        React.createElement(StudentDetailDialog, { isOpen: selectedStudent !== null, studentId: (_a = selectedStudent === null || selectedStudent === void 0 ? void 0 : selectedStudent.studentId) !== null && _a !== void 0 ? _a : '', studentName: (_b = selectedStudent === null || selectedStudent === void 0 ? void 0 : selectedStudent.name) !== null && _b !== void 0 ? _b : '', classProfileId: selectedClassId, className: selectedClassName, classStartDate: (_c = selectedClass === null || selectedClass === void 0 ? void 0 : selectedClass.startDateUtc) !== null && _c !== void 0 ? _c : '', classEndDate: (_d = selectedClass === null || selectedClass === void 0 ? void 0 : selectedClass.endDateUtc) !== null && _d !== void 0 ? _d : '', onClose: onClose, goBack: handleStudentDetailClose })));
}
export const SyncGradesInfo = () => {
    return (React.createElement(T.Text.P, { automationId: 'manualSyncDescription' },
        "Although your LTI integration automatically syncs grades, you can use this tool to manually sync grades to your LMS's gradebook. If grades are still not syncing, visit our",
        ' ',
        React.createElement(T.Link, { href: TROUBLESHOOTING_URL, target: '_blank', automationId: 'troubleshootingLink' }, "troubleshooting page"),
        ' ',
        "or",
        ' ',
        React.createElement(T.Link, { href: CONTACT_SUPPORT_URL, target: '_blank', automationId: 'contactSupportLink' }, "contact support"),
        ". Grade sync may take some time to complete. Please check back later to confirm that grades have synced successfully."));
};
export default ManualSyncDialog;
//# sourceMappingURL=ManualSyncDialog.js.map